import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { VOICE_PROFILE_URL } from '@utils/api/endpoints.voice-profiles.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { VOICE_PROFILE_QUERY_KEYS } from './query-keys.ts';

interface DeleteOnePayload {
  speakerId: number;
  profileId: number;
}

/** Delete one enrolled sample, leaving the speaker's others intact.
 *  Deletion stays available even when the feature is switched off, so an
 *  organization can always withdraw biometric data it has stored. */
export const useDeleteVoiceSample = () => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError<BaseError>, DeleteOnePayload>(
    async ({ speakerId, profileId }) => {
      await Axios.delete(VOICE_PROFILE_URL.deleteSample(speakerId, profileId));
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(
          VOICE_PROFILE_QUERY_KEYS.samples(variables.speakerId)
        );
      },
      useErrorBoundary: false
    }
  );
};

/** Clear every sample for a speaker.
 *
 * A separate route and a separate hook from the single-sample delete: the
 * backend deliberately refuses to let a caller clear a whole enrollment by
 * accident, and the UI asks for a distinct confirmation naming how many
 * samples go. */
export const useClearVoiceSamples = () => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError<BaseError>, { speakerId: number }>(
    async ({ speakerId }) => {
      await Axios.delete(VOICE_PROFILE_URL.clearSamples(speakerId));
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(
          VOICE_PROFILE_QUERY_KEYS.samples(variables.speakerId)
        );
      },
      useErrorBoundary: false
    }
  );
};
